import type { Piece, Player, Corner } from '@sheshdash/shared';

/** Derive piece counts from a player's pieces array */
export function getPieceCounts(pieces: Piece[]): { reserve: number; board: number; center: number } {
  let reserve = 0;
  let board = 0;
  let center = 0;
  for (const p of pieces) {
    if (p.state === 'in_corner') reserve++;
    else if (p.state === 'in_center') center++;
    else board++;
  }
  return { reserve, board, center };
}

/** Map server corner to skin color config */
export interface CornerConfig {
  colorHex: string;
  color: string;
  startPos: { row: number; col: number };
  diagonalPath: { row: number; col: number }[];
}

const CORNER_CONFIGS: Record<Corner, CornerConfig> = {
  top_left: {
    color: 'red',
    colorHex: '#ef4444',
    startPos: { row: 0, col: 0 },
    diagonalPath: [
      { row: 0, col: 0 },
      { row: 1, col: 1 },
      { row: 2, col: 2 },
      { row: 3, col: 3 },
    ],
  },
  top_right: {
    color: 'green',
    colorHex: '#10b981',
    startPos: { row: 0, col: 7 },
    diagonalPath: [
      { row: 0, col: 7 },
      { row: 1, col: 6 },
      { row: 2, col: 5 },
      { row: 3, col: 4 },
    ],
  },
  bottom_right: {
    color: 'blue',
    colorHex: '#3b82f6',
    startPos: { row: 7, col: 7 },
    diagonalPath: [
      { row: 7, col: 7 },
      { row: 6, col: 6 },
      { row: 5, col: 5 },
      { row: 4, col: 4 },
    ],
  },
  bottom_left: {
    color: 'orange',
    colorHex: '#f59e0b',
    startPos: { row: 7, col: 0 },
    diagonalPath: [
      { row: 7, col: 0 },
      { row: 6, col: 1 },
      { row: 5, col: 2 },
      { row: 4, col: 3 },
    ],
  },
};

const PLAYER_COLOR_HEX: Record<string, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#10b981',
  orange: '#f59e0b',
};

export function getCornerConfig(corner: Corner): CornerConfig {
  return CORNER_CONFIGS[corner];
}

/** Final center cell for a player's corner. */
export function getCenterCellForCorner(corner: Corner): { row: number; col: number } {
  return CORNER_CONFIGS[corner].diagonalPath[3];
}

/** Get player color hex - use server color or fall back to corner config */
export function getPlayerColorHex(player: Player): string {
  // Server may send either CSS hex or logical game color names.
  if (player.color && player.color.startsWith('#')) return player.color;
  const normalizedColor = player.color?.trim().toLowerCase();
  if (normalizedColor && PLAYER_COLOR_HEX[normalizedColor]) {
    return PLAYER_COLOR_HEX[normalizedColor];
  }
  return getCornerConfig(player.corner).colorHex;
}

/** Format ISO turn deadline string to remaining seconds */
export function getTimeRemaining(turnDeadline?: string): number {
  if (!turnDeadline) return 0;
  const deadline = new Date(turnDeadline).getTime();
  if (Number.isNaN(deadline)) return 0;
  const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  return remaining;
}

export const DEFAULT_TURN_TIMEOUT_SECONDS = 30;

export interface TurnCountdownTickCheckInput {
  timeLeft: number;
  lastPlayedSecond: number | null;
  isCurrentUserTurn: boolean;
  isHistoryMode: boolean;
  hasDeadline: boolean;
  isPlaying: boolean;
}

/** Determine whether countdown tick should be played on this update. */
export function shouldPlayTurnCountdownTick(input: TurnCountdownTickCheckInput): boolean {
  if (!input.isPlaying || !input.hasDeadline || !input.isCurrentUserTurn || input.isHistoryMode) {
    return false;
  }
  if (input.timeLeft <= 0 || input.timeLeft > 5) {
    return false;
  }
  return input.lastPlayedSecond !== input.timeLeft;
}

/** Map corner to reserve rail position (which side of the board) */
export type RailSide = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export function getReserveRailSide(corner: Corner): RailSide {
  switch (corner) {
    case 'top_left': return 'top-left';
    case 'top_right': return 'top-right';
    case 'bottom_right': return 'bottom-right';
    case 'bottom_left': return 'bottom-left';
  }
}

/** Format seconds as MM:SS */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

/** Get the color hex for a piece, looking up the owning player first */
export function getPieceColorHex(piece: Piece & { corner: Corner }, players: Player[]): string {
  const player = players.find(p => p.id === piece.owner_id);
  if (player) return getPlayerColorHex(player);
  return getCornerConfig(piece.corner).colorHex;
}

/** Board dimension constants */
export const BOARD_SIZE = 8;
export const BOARD_DIM = 500;
export const CELL_SIZE = BOARD_DIM / BOARD_SIZE;
export const PIECE_SIZE = 48;
export const PIECE_OFFSET = (CELL_SIZE - PIECE_SIZE) / 2;

/** Convert board position to pixel coordinates for absolute positioning */
export function getCellCoords(row: number, col: number): { x: number; y: number } {
  return { x: col * CELL_SIZE + PIECE_OFFSET, y: row * CELL_SIZE + PIECE_OFFSET };
}

/** Get reserve piece coordinates (side rails on desktop) */
export function getReserveCoords(corner: Corner, index: number): { x: number; y: number } {
  const leftX = -85;
  const rightX = BOARD_DIM + 35;

  switch (corner) {
    case 'top_left':
      return { x: leftX, y: index * CELL_SIZE + PIECE_OFFSET };
    case 'bottom_left':
      return { x: leftX, y: (index + 4) * CELL_SIZE + PIECE_OFFSET };
    case 'top_right':
      return { x: rightX, y: index * CELL_SIZE + PIECE_OFFSET };
    case 'bottom_right':
      return { x: rightX, y: (index + 4) * CELL_SIZE + PIECE_OFFSET };
  }
}

/** Chess notation for a board position */
export function posToNotation(row: number, col: number): string {
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  const numbers = ['8', '7', '6', '5', '4', '3', '2', '1'];
  return `${letters[col]}${numbers[row]}`;
}

/** Board legend labels */
export const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
export const NUMBERS = ['8', '7', '6', '5', '4', '3', '2', '1'];

/** Center cell check */
export function isCenterCell(row: number, col: number): boolean {
  return (row === 3 || row === 4) && (col === 3 || col === 4);
}

/** Check if a cell is a player's start position */
export function getStartCornerAtCell(row: number, col: number): Corner | null {
  for (const [corner, config] of Object.entries(CORNER_CONFIGS)) {
    if (config.startPos.row === row && config.startPos.col === col) {
      return corner as Corner;
    }
  }
  return null;
}

/** Check if a cell is on a player's diagonal path (not start or center) */
export function getDiagonalCornerAtCell(row: number, col: number): Corner | null {
  for (const [corner, config] of Object.entries(CORNER_CONFIGS)) {
    // Check intermediate diagonal cells (index 1 and 2)
    if (
      (config.diagonalPath[1].row === row && config.diagonalPath[1].col === col) ||
      (config.diagonalPath[2].row === row && config.diagonalPath[2].col === col)
    ) {
      return corner as Corner;
    }
  }
  return null;
}
